import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormGroup } from "@cofano/cck";
import { FormDialog } from "components/dialogs/Dialogs";
import ApplicationLanguageSelect, { ApplicationLanguageSelectOption } from "components/selects/ApplicationLanguageSelect";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import { templateLanguageSchema, TemplateLanguageSchemaInput, TemplateLanguageSchemaOutput } from "view/templates/utils/template.schema";
import { Language } from "utils";

const TemplateAddLanguageDialog = ({ open, onClose, onAdd, existingLanguages }: {
	open: boolean,
	onClose: () => void,
	onAdd: (language: TemplateLanguageSchemaOutput["language"]) => void,
	existingLanguages?: ApplicationLanguageSelectOption[],
}) => {

	const { control, handleSubmit, reset } = useForm<TemplateLanguageSchemaInput, unknown, TemplateLanguageSchemaOutput>({
		resolver: zodResolver(templateLanguageSchema),
		defaultValues: {
			language: null,
		},
	});

	const handleOnClose = () => {
		onClose();
		reset();
	};

	const save = (data: TemplateLanguageSchemaOutput) => {
		onAdd(data.language);
		handleOnClose();
	};

	const existingLanguageCodes = existingLanguages?.map(language => language.code);

	return (
		<FormDialog
			form="template-add-language-dialog-form"
			open={open}
			onClose={handleOnClose}
			title={Language("TEMPLATES_ADD_LANGUAGE")}
			confirmText={intl.formatMessage({ id: "ADD_BUTTON" })}>
			<form
				id="template-add-language-dialog-form"
				onSubmit={(event) => {
					event.stopPropagation();
					handleSubmit(save)(event);
				}}>
				<FormGroup
					label={Language("TEMPLATES_LANGUAGE")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="language"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<ApplicationLanguageSelect
									{...field}
									getOptionDisabled={existingLanguageCodes ? (option) => existingLanguageCodes.includes(option.code) : undefined}
								/>
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)}
					/>
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default TemplateAddLanguageDialog;
